import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Input } from '@/components/ui/input';
import { ReorderProps } from '@/modules/rules/hooks/useRules';
import { isKeyEnter } from '@/util/util';

interface OrderInputProps {
  currentPosition: number;
  totalRules: number;
  ruleId: string;
  disabled: boolean;
  onReorder: (params: ReorderProps) => void;
}

export const OrderInput = ({
  currentPosition,
  totalRules,
  ruleId,
  disabled,
  onReorder,
}: OrderInputProps) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(String(currentPosition));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (inputValue === '' || /^\d+$/.test(inputValue)) {
      setValue(inputValue);
    }
  };

  const handleSubmit = () => {
    const newPosition = parseInt(value, 10);
    if (!isNaN(newPosition) && newPosition >= 1) {
      const clampedPosition = Math.min(newPosition, totalRules);
      if (clampedPosition !== currentPosition) {
        onReorder({ rule_id: ruleId, position: clampedPosition });
      } else {
        setValue(String(currentPosition));
      }
    } else {
      setValue(String(currentPosition));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isKeyEnter(e)) {
      e.currentTarget.blur();
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <InfoTooltip content={t('rules.order_tooltip')} side="top">
      <Input
        aria-label={t('rules.order_input')}
        inputMode="numeric"
        className="h-11 w-11 shrink-0 border-border bg-background p-2 text-center text-foreground"
        value={value}
        onChange={handleChange}
        onBlur={handleSubmit}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
        disabled={disabled}
      />
    </InfoTooltip>
  );
};
